///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
    DesignQuotationRequestDTO,
    EngineAntennaDTO,
    EngineProductDTO,
    ExtendedProductdata,
    GtthKeyValueDTO,
    PlannerFeatureCollectionDTO,
    ProjectDTO
} from '../../generated/shared-types.model';

import { isDefined } from '@siklu/sui-lib/utils';

@Injectable()
export class ProjectService {
    private readonly fetchUrl = 'api/projects';
    private readonly modifyUrl = 'api/project';
    private readonly topologyUrl = 'api/topology';
    private readonly notificationsUrl = 'api/notification';

    constructor(private readonly http: HttpClient) {}

    // +++++++++++++++++ old api ++++++++++++++++++++++

    create(project: ProjectDTO, latitude: number, longitude: number, zoomLevel: number): Observable<ProjectDTO> {
        const copy: ProjectDTO = { ...project };
        return this.http
            .post<ProjectDTO>(`${this.modifyUrl}/${latitude}/${longitude}/${zoomLevel}`, copy)
            .pipe(catchError(() => of(undefined)));
    }

    cloneProject(id: number, parentFolderId: number, newName: string): Observable<number> {
        return this.http.put<number>(`${this.modifyUrl}/${id}/${parentFolderId}/clone`, newName);
    }

    migrateProject(id: number, parentFolderId: number, newName: string): Observable<number> {
        return this.http.put<number>(`${this.modifyUrl}/${id}/${parentFolderId}/migrate`, newName);
    }

    //=====================================================

    update(project: ProjectDTO): Observable<ProjectDTO> {
        const copy: ProjectDTO = { ...project };
        return this.http.put<ProjectDTO>(this.modifyUrl, copy).pipe(catchError(() => of(undefined)));
    }

    getProject(id: number): Observable<ProjectDTO> {
        return this.http.get<ProjectDTO>(`${this.fetchUrl}/${id}`).pipe(catchError(() => of(undefined)));
    }

    getProjects(folderId: number, filter?: string): Observable<ProjectDTO[]> {
        let params: HttpParams = new HttpParams();
        if (filter) {
            params = params.append('filter', filter);
        }
        return this.http.get<ProjectDTO[]>(`${this.fetchUrl}/folder/${folderId}`, { params }).pipe(catchError(() => of([])));
    }

    deleteProject(id: number): Observable<any> {
        return this.http.delete(`${this.modifyUrl}/${id}`).pipe(catchError(() => of(undefined)));
    }

    getEngineProducts(): Observable<EngineProductDTO[]> {
        return this.http.get<EngineProductDTO[]>(`api/engine-products`).pipe(catchError(() => of([])));
    }

    changeEngineProductsPriority(radioIdPriorityList: GtthKeyValueDTO[]): Observable<EngineProductDTO[]> {
        return this.http.put<EngineProductDTO[]>(`api/engine-products/priority`, radioIdPriorityList).pipe(catchError(() => of([])));
    }

    getEngineAntennas(): Observable<EngineAntennaDTO[]> {
        return this.http.get<EngineAntennaDTO[]>(`api/engine-antennas`).pipe(catchError(() => of([])));
    }

    requestDesignQuotation(projectId: number, reportObject: DesignQuotationRequestDTO): Observable<any> {
        return this.http.post(`${this.notificationsUrl}/${projectId}/design-quotation`, reportObject).pipe(
            map((res: HttpResponse<any>) => {
                return res;
            })
        );
    }

    getLinksProducts(projectId?: number, customerId?: number): Observable<EngineProductDTO[]> {
        let params = new HttpParams();
        if (isDefined(projectId)) {
            params = params.set('projectId', projectId);
        }
        if (isDefined(customerId)) {
            params = params.set('customerId', customerId);
        }
        return this.http
            .get<EngineProductDTO[]>(`${this.topologyUrl}/link-products`, {
                params
            })
            .pipe(catchError(() => of([])));
    }

    getDefaultProducts(projectId?: number, customerId?: number): Observable<ExtendedProductdata[][]> {
        let params = new HttpParams();
        if (isDefined(projectId)) {
            params = params.set('projectId', projectId);
        }
        if (isDefined(customerId)) {
            params = params.set('customerId', customerId);
        }
        return this.http
            .get<ExtendedProductdata[][]>(`${this.topologyUrl}/default-products`, {
                params
            })
            .pipe(catchError(() => of([])));
    }

    updateProjectPdbConfig(projectId: number, engineRadioConfig: EngineProductDTO[]): Observable<boolean> {
        return this.http.post<any>(`${this.topologyUrl}/engine-products/${projectId}`, engineRadioConfig).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    temp_getProjectLogicConfig(id: number): Observable<PlannerFeatureCollectionDTO> {
        return this.http
            .get<PlannerFeatureCollectionDTO>(`${this.topologyUrl}/${id}/logic-config`)
            .pipe(catchError(() => of({} as PlannerFeatureCollectionDTO)));
    }

    temp_updateProjectMainConfig(id: number, json: PlannerFeatureCollectionDTO): Observable<PlannerFeatureCollectionDTO> {
        return this.http
            .put<PlannerFeatureCollectionDTO>(`${this.topologyUrl}/${id}/main-config`, json)
            .pipe(catchError(() => of(undefined)));
    }

    canBeModified(id: number): Observable<boolean> {
        return this.http.get<boolean>(`${this.topologyUrl}/${id}/is-not-locked`).pipe(
            map((response: any) => {
                if (response && response.notLocked !== undefined) {
                    return response.notLocked;
                } else {
                    return false;
                }
            }),
            catchError(() => of(false))
        );
    }
}
